import { Component, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  MonitoringService,
  MonitoringFinding,
  FindingSeverity,
} from '../../core/services/monitoring.service';
import { CloudService, QuotaInfo } from '../../core/services/cloud.service';
import { LinkedTicketsService } from '../../core/services/linked-tickets.service';

const SEVERITY_ORDER: Record<FindingSeverity, number> = {
  CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3, INFO: 4,
};

interface ServiceHealthRow {
  type:        string;
  label:       string;
  icon:        string;
  route:       string;
  total:       number;
  critical:    number;
  statusClass: string;
}

interface TrendBar {
  label:  string;
  count:  number;
  height: number;
}

interface ActivityEvent {
  icon:         string;
  iconBg:       string;
  iconColor:    string;
  message:      string;
  relativeTime: string;
  sortTime:     number;
}

@Component({
  selector: 'app-dashboard-home',
  templateUrl: './dashboard-home.component.html',
})
export class DashboardHomeComponent implements OnInit, OnDestroy {
  // ── Load state ────────────────────────────────────────────────────────────
  loading = true;
  error   = false;
  lastRefreshedAt: string | null = null;

  // ── KPIs ──────────────────────────────────────────────────────────────────
  openCount        = 0;
  criticalCount    = 0;
  medianAgeDays: number | null = null;
  resolvedThisWeek = 0;

  // ── Action Required ───────────────────────────────────────────────────────
  actionRequired: MonitoringFinding[] = [];

  // ── Ticket Coverage ───────────────────────────────────────────────────────
  openResourceCount = 0;
  ticketedCount     = 0;
  coveragePercent   = 0;
  ghTicketCount     = 0;
  jiraTicketCount   = 0;

  // ── Service Health ────────────────────────────────────────────────────────
  serviceHealth: ServiceHealthRow[] = [];

  // ── Trends ────────────────────────────────────────────────────────────────
  trendBars:     TrendBar[] = [];
  maxTrendCount  = 0;

  // ── Stuck / Friction ──────────────────────────────────────────────────────
  stuckFindings: MonitoringFinding[] = [];

  // ── Recent Activity ───────────────────────────────────────────────────────
  recentActivity: ActivityEvent[] = [];

  // ── Quota ─────────────────────────────────────────────────────────────────
  quota: QuotaInfo | null = null;

  private sub?: Subscription;

  constructor(
    private readonly monitoring:     MonitoringService,
    private readonly cloud:          CloudService,
    private readonly linkedTickets:  LinkedTicketsService,
  ) {}

  ngOnInit(): void { this.load(); }

  ngOnDestroy(): void { this.sub?.unsubscribe(); }

  load(): void {
    this.loading = true;
    this.error   = false;
    this.sub?.unsubscribe();

    this.sub = forkJoin({
      findings:    this.monitoring.getFindings(),
      ghTickets:   this.linkedTickets.listGitHubTickets().pipe(catchError(() => of({ tickets: [] as never[] }))),
      jiraTickets: this.linkedTickets.listJiraTickets().pipe(catchError(() => of({ tickets: [] as never[] }))),
    }).subscribe({
      next: ({ findings, ghTickets, jiraTickets }) => {
        const open = findings.filter(f => f.findingState === 'OPEN');
        this.loading        = false;
        this.lastRefreshedAt = new Date().toISOString();

        this.computeKPIs(findings, open);
        this.computeActionRequired(open);
        this.computeServiceHealth(open);
        this.computeTrends(findings);
        this.computeStuck(open);
        this.computeActivity(findings);
        this.computeTicketCoverage(open, ghTickets.tickets ?? [], jiraTickets.tickets ?? []);
        this.loadQuota();
      },

      error: () => {
        this.loading = false;
        this.error   = true;
      },
    });
  }

  private loadQuota(): void {
    this.cloud.loadMonitoringState().subscribe({
      next: () => { this.quota = this.cloud.getQuotaInfo(); },
    });
  }

  // ── Computation ───────────────────────────────────────────────────────────

  private computeKPIs(all: MonitoringFinding[], open: MonitoringFinding[]): void {
    this.openCount     = open.length;
    this.criticalCount = open.filter(f => f.severity === 'CRITICAL').length;

    if (open.length > 0) {
      const now  = Date.now();
      const ages = open
        .map(f => (now - new Date(f.observedAt).getTime()) / 86_400_000)
        .sort((a, b) => a - b);
      const mid = Math.floor(ages.length / 2);
      this.medianAgeDays = ages.length % 2 === 0
        ? (ages[mid - 1] + ages[mid]) / 2
        : ages[mid];
    } else {
      this.medianAgeDays = null;
    }

    const weekAgo = Date.now() - 7 * 86_400_000;
    this.resolvedThisWeek = all.filter(
      f => f.findingState === 'RESOLVED' && f.resolvedAt &&
           new Date(f.resolvedAt).getTime() > weekAgo,
    ).length;
  }

  private computeActionRequired(open: MonitoringFinding[]): void {
    this.actionRequired = [...open]
      .sort((a, b) => {
        const sevDiff = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
        if (sevDiff !== 0) return sevDiff;
        return new Date(a.observedAt).getTime() - new Date(b.observedAt).getTime();
      })
      .slice(0, 8);
  }

  private computeServiceHealth(open: MonitoringFinding[]): void {
    const defs = [
      { type: 'LAMBDA',         label: 'Lambda',         icon: 'zap',        route: '/cloud/lambda' },
      { type: 'GLUE',           label: 'Glue',           icon: 'database',   route: '/cloud/glue' },
      { type: 'PIPELINE',       label: 'CodePipeline',   icon: 'git-branch', route: '/cloud/codepipeline' },
      { type: 'STEP_FUNCTIONS', label: 'Step Functions', icon: 'git-graph',  route: '/cloud/step-functions' },
      { type: 'ECS_SERVICE',    label: 'ECS',            icon: 'box',        route: '/cloud/ecs' },
      { type: 'RDS_INSTANCE',   label: 'RDS',            icon: 'database',   route: '/cloud/rds' },
      { type: 'API_GATEWAY',    label: 'API Gateway',    icon: 'globe',      route: '/cloud/api-gateway' },
      { type: 'EC2_INSTANCE',   label: 'EC2',            icon: 'server',     route: '/cloud/ec2' },
      { type: 'COGNITO',        label: 'Cognito',        icon: 'users',      route: '/cloud/cognito' },
      { type: 'APPSYNC',        label: 'AppSync',        icon: 'workflow',   route: '/cloud/appsync' },
      { type: 'DYNAMODB',       label: 'DynamoDB',       icon: 'table',      route: '/cloud/dynamodb' },
      { type: 'SQS',            label: 'SQS',            icon: 'inbox',      route: '/cloud/sqs' },
      { type: 'EVENTBRIDGE',    label: 'EventBridge',    icon: 'radio',      route: '/cloud/eventbridge' },
      { type: 'ALB',            label: 'ALB',            icon: 'network',    route: '/cloud/alb' },
      { type: 'SNS',            label: 'SNS',            icon: 'megaphone',  route: '/cloud/sns' },
      { type: 'CLOUDFRONT',     label: 'CloudFront',     icon: 'cloud',      route: '/cloud/cloudfront' },
    ];
    this.serviceHealth = defs.map(def => {
      const findings = open.filter(f => f.serviceType === def.type);
      const critical = findings.filter(f => f.severity === 'CRITICAL').length;
      return {
        ...def,
        total:    findings.length,
        critical,
        statusClass: critical > 0
          ? 'bg-red-50 text-red-700'
          : findings.length > 0
            ? 'bg-amber-50 text-amber-700'
            : 'bg-emerald-50 text-emerald-700',
      };
    });
  }

  private computeTrends(all: MonitoringFinding[]): void {
    const now  = new Date();
    const bars: TrendBar[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().slice(0, 10);
      const count   = all.filter(f => f.createdAt.slice(0, 10) === dateStr).length;
      bars.push({
        label: d.toLocaleDateString('en-US', { weekday: 'short' }),
        count,
        height: 0,
      });
    }
    const max      = Math.max(...bars.map(b => b.count), 1);
    this.maxTrendCount = max;
    this.trendBars = bars.map(b => ({
      ...b,
      height: Math.round((b.count / max) * 72),
    }));
  }

  private computeStuck(open: MonitoringFinding[]): void {
    const staleMs = 24 * 3_600_000;
    const now     = Date.now();
    this.stuckFindings = [...open]
      .filter(f => now - new Date(f.observedAt).getTime() > staleMs)
      .sort((a, b) => new Date(a.observedAt).getTime() - new Date(b.observedAt).getTime())
      .slice(0, 6);
  }

  private computeActivity(all: MonitoringFinding[]): void {
    type Event = ActivityEvent;
    const events: Event[] = [];

    for (const f of all) {
      if (f.findingState === 'RESOLVED' && f.resolvedAt) {
        events.push({
          icon:         'circle-check',
          iconBg:       'bg-emerald-100',
          iconColor:    'text-emerald-600',
          message:      `Resolved: ${f.title}`,
          relativeTime: this.relativeTime(f.resolvedAt),
          sortTime:     new Date(f.resolvedAt).getTime(),
        });
      }
      events.push({
        icon:         sevIcon(f.severity),
        iconBg:       sevIconBg(f.severity),
        iconColor:    sevIconColor(f.severity),
        message:      f.title,
        relativeTime: this.relativeTime(f.createdAt),
        sortTime:     new Date(f.createdAt).getTime(),
      });
    }

    this.recentActivity = events
      .sort((a, b) => b.sortTime - a.sortTime)
      .slice(0, 8);
  }

  private computeTicketCoverage(
    open: MonitoringFinding[],
    ghTickets: { configId: string }[],
    jiraTickets: { configId: string }[],
  ): void {
    const openConfigIds    = new Set(open.map(f => f.configId));
    const ticketedConfigIds = new Set([
      ...ghTickets.map(t => t.configId),
      ...jiraTickets.map(t => t.configId),
    ]);

    const total   = openConfigIds.size;
    const covered = [...openConfigIds].filter(id => ticketedConfigIds.has(id)).length;

    this.openResourceCount = total;
    this.ticketedCount     = covered;
    this.coveragePercent   = total === 0 ? 100 : Math.round((covered / total) * 100);
    this.ghTicketCount     = ghTickets.filter(t => openConfigIds.has(t.configId)).length;
    this.jiraTicketCount   = jiraTickets.filter(t => openConfigIds.has(t.configId)).length;
  }

  // ── View helpers ──────────────────────────────────────────────────────────

  relativeTime(iso: string): string {
    const diff  = Date.now() - new Date(iso).getTime();
    const mins  = Math.floor(diff / 60_000);
    if (mins < 1)  return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)  return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return `${Math.floor(days / 30)}mo ago`;
  }

  ageDays(iso: string): number {
    return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  }

  formatAge(iso: string): string {
    const days = this.ageDays(iso);
    if (days === 0) return '< 1 day';
    if (days === 1) return '1 day';
    return `${days} days`;
  }

  formatMedianAge(days: number): string {
    if (days < 1) return '< 1 day';
    if (days < 2) return '1 day';
    return `${Math.round(days)} days`;
  }

  serviceRoute(serviceType: string): string {
    const map: Record<string, string> = {
      LAMBDA:         '/cloud/lambda',
      GLUE:           '/cloud/glue',
      PIPELINE:       '/cloud/codepipeline',
      STEP_FUNCTIONS: '/cloud/step-functions',
      ECS_SERVICE:    '/cloud/ecs',
      RDS_INSTANCE:   '/cloud/rds',
      API_GATEWAY:    '/cloud/api-gateway',
      EC2_INSTANCE:   '/cloud/ec2',
      COGNITO:        '/cloud/cognito',
      APPSYNC:        '/cloud/appsync',
      DYNAMODB:       '/cloud/dynamodb',
      SQS:            '/cloud/sqs',
      EVENTBRIDGE:    '/cloud/eventbridge',
      ALB:            '/cloud/alb',
      SNS:            '/cloud/sns',
      CLOUDFRONT:     '/cloud/cloudfront',
    };
    return map[serviceType] ?? '/cloud';
  }

  severityBadgeClass(severity: FindingSeverity): string {
    switch (severity) {
      case 'CRITICAL': return 'bg-red-100 text-red-700';
      case 'HIGH':     return 'bg-orange-100 text-orange-700';
      case 'MEDIUM':   return 'bg-amber-100 text-amber-700';
      case 'LOW':      return 'bg-blue-100 text-blue-700';
      default:         return 'bg-slate-100 text-slate-600';
    }
  }

  severityDotClass(severity: FindingSeverity): string {
    switch (severity) {
      case 'CRITICAL': return 'bg-red-500';
      case 'HIGH':     return 'bg-orange-400';
      case 'MEDIUM':   return 'bg-amber-400';
      case 'LOW':      return 'bg-blue-400';
      default:         return 'bg-slate-300';
    }
  }

  serviceTypeLabel(t: string): string {
    const map: Record<string, string> = { LAMBDA: 'Lambda', GLUE: 'Glue', PIPELINE: 'CodePipeline', STEP_FUNCTIONS: 'Step Functions', ECS_SERVICE: 'ECS', RDS_INSTANCE: 'RDS', API_GATEWAY: 'API Gateway', EC2_INSTANCE: 'EC2', COGNITO: 'Cognito', APPSYNC: 'AppSync', DYNAMODB: 'DynamoDB', SQS: 'SQS', EVENTBRIDGE: 'EventBridge', ALB: 'ALB', SNS: 'SNS', CLOUDFRONT: 'CloudFront' };
    return map[t] ?? t;
  }

  get quotaPercent(): number {
    return this.quota?.percentUsed ?? 0;
  }

  get quotaBarClass(): string {
    const p = this.quotaPercent;
    if (p >= 90) return 'bg-red-500';
    if (p >= 70) return 'bg-amber-400';
    return 'bg-violet-500';
  }
}

// ── Severity icon helpers (module-scope) ──────────────────────────────────────

function sevIcon(s: FindingSeverity): string {
  return s === 'CRITICAL' || s === 'HIGH' ? 'triangle-alert' : 'circle-alert';
}

function sevIconBg(s: FindingSeverity): string {
  switch (s) {
    case 'CRITICAL': return 'bg-red-100';
    case 'HIGH':     return 'bg-orange-100';
    case 'MEDIUM':   return 'bg-amber-100';
    default:         return 'bg-slate-100';
  }
}

function sevIconColor(s: FindingSeverity): string {
  switch (s) {
    case 'CRITICAL': return 'text-red-600';
    case 'HIGH':     return 'text-orange-500';
    case 'MEDIUM':   return 'text-amber-500';
    default:         return 'text-slate-400';
  }
}
